import { useState, useEffect } from "react";
import { PaziaModal } from "components/molecules";
import { PaziaLoader } from "components/atoms";
import { useCourseGroup, useCourseGroupTrainers, useUpdateTrainerBlacklist } from "api/coursegroups";
import { useToaster } from "utils";

interface Props {
  courseGroupId: number;
  courseGroupName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const TrainerBlacklistModal = ({ courseGroupId, courseGroupName, isOpen, onClose }: Props) => {
  const [blacklistedIds, setBlacklistedIds] = useState<Set<number>>(new Set());
  const { showSuccess, showError } = useToaster();

  const id = courseGroupId.toString();
  const { courseGroup, isLoading: isLoadingGroup } = useCourseGroup(id);
  const { trainers, isLoading: isLoadingTrainers } = useCourseGroupTrainers(id);
  const updateBlacklist = useUpdateTrainerBlacklist(courseGroupId);

  useEffect(() => {
    if (courseGroup?.blacklistedTrainerIds) {
      const ids = courseGroup.blacklistedTrainerIds
        .split(",")
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
      setBlacklistedIds(new Set(ids));
    } else {
      setBlacklistedIds(new Set());
    }
  }, [courseGroup]);

  const handleToggle = (trainerId: number) => {
    setBlacklistedIds((prev) => {
      const next = new Set(prev);
      if (next.has(trainerId)) next.delete(trainerId);
      else next.add(trainerId);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      const ids = Array.from(blacklistedIds);
      await updateBlacklist.mutateAsync({
        blacklistedTrainerIds: ids.length > 0 ? ids.join(",") : undefined,
      });
      showSuccess("Zapisano");
      onClose();
    } catch {
      showError("Nie udało się zapisać");
    }
  };

  const isLoading = isLoadingGroup || isLoadingTrainers;

  return (
    <PaziaModal id="trainer-blacklist-modal" isOpen={isOpen} onCloseRequest={onClose} size="md">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1">
          Widoczność wykładowców
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{courseGroupName}</p>

        {isLoading ? (
          <PaziaLoader position="center" />
        ) : trainers.length === 0 ? (
          <p className="text-sm text-slate-400 py-4">Brak wykładowców przypisanych do tej grupy kursów.</p>
        ) : (
          <ul className="space-y-2 mb-6">
            {trainers.map((trainer) => (
              <li key={trainer.id} className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id={`trainer-${trainer.id}`}
                  checked={!blacklistedIds.has(trainer.id)}
                  onChange={() => handleToggle(trainer.id)}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <label
                  htmlFor={`trainer-${trainer.id}`}
                  className="text-sm text-slate-700 dark:text-slate-200 cursor-pointer"
                >
                  {trainer.firstName} {trainer.surname}
                  {trainer.email && (
                    <span className="text-slate-400 ml-1">({trainer.email})</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={updateBlacklist.isPending || isLoading}
            className="px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            {updateBlacklist.isPending ? "Zapisywanie..." : "Zapisz"}
          </button>
        </div>
      </div>
    </PaziaModal>
  );
};
